import type { Gym } from "@api/types";
import { Download, Loader2, MoreHorizontal, Plus, Upload } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { toast } from "sonner";
import { ApiError, handleApiError } from "@/api/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import * as analytics from "@/lib/analytics";
import { exportContactsToCsv } from "./export-contacts";

export interface ContactsHeaderActionsProps {
  gym: Gym;
  onCreate: () => void;
}

export function ContactsHeaderActions(props: ContactsHeaderActionsProps) {
  const [isExporting, setIsExporting] = useState(false);

  async function handleExport() {
    setIsExporting(true);
    try {
      const count = await exportContactsToCsv(props.gym.id);
      analytics.track("Contacts Exported", { count, gym: props.gym.name });
      toast.success(
        count === 0 ? "No contacts to export" : `Exported ${count.toLocaleString()} contacts`,
      );
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err);
      } else {
        toast.error("Failed to export contacts");
      }
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="flex items-center ml-3 space-x-2">
      <Button onClick={props.onCreate} variant="default" size="sm">
        <Plus className="w-3 h-3 mr-2" aria-hidden="true" />
        Create contact
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="More actions"
            disabled={isExporting}
            className="px-2"
          >
            {isExporting ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <MoreHorizontal className="size-4" aria-hidden="true" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem asChild className="gap-2">
            <Link href="/contacts/import">
              <Upload className="size-3.5" aria-hidden="true" />
              Import contacts
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleExport} className="gap-2" disabled={isExporting}>
            <Download className="size-3.5" aria-hidden="true" />
            Export contacts
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
